/**
 * FounderRail — persistent right column: current focus, next gate,
 * next decision, blockers, launch-critical remaining, recently completed.
 */
import { Target, ShieldCheck, Sparkles, OctagonX, CheckCircle2, ArrowRight } from "lucide-react";
import { StatusPill } from "./StatusPill";

function Section({ testid, label, Icon, children }) {
    return (
        <div data-testid={testid} className="qoryn-panel rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
                <Icon className="h-3.5 w-3.5 text-qoryn-cyan" strokeWidth={1.75} />
                <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                    {label}
                </div>
            </div>
            {children}
        </div>
    );
}

function MilestoneLine({ m }) {
    return (
        <div className="text-[12.5px] text-qoryn-text leading-snug">
            <div className="truncate">{m.title}</div>
            <div className="mt-1"><StatusPill status={m.status} size="xs" /></div>
        </div>
    );
}

export default function FounderRail({ milestones }) {
    const currentFocus = milestones.find(
        (m) => m.status === "FOUNDER_REVIEW" && m.launch_critical,
    ) ?? milestones.find((m) => m.status === "IN_PROGRESS");
    const nextGate = milestones.find(
        (m) => m.launch_critical && ["FOUNDER_REVIEW", "IN_PROGRESS"].includes(m.status),
    );
    const nextDecision = milestones.find(
        (m) => m.launch_critical && m.status === "LOCKED",
    );
    const blockers = milestones.filter((m) => m.status === "BLOCKED");
    const launchCriticalRemaining = milestones.filter(
        (m) => m.launch_critical && !["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status),
    );
    const recentCompleted = milestones
        .filter((m) => ["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status) && m.completed_at)
        .slice()
        .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""))
        .slice(0, 4);

    return (
        <div data-testid="gtm-founder-rail" className="space-y-3">
            <Section testid="gtm-rail-current-focus" label="Current Focus" Icon={Target}>
                {currentFocus ? <MilestoneLine m={currentFocus} /> : <div className="text-[12px] text-qoryn-textDim">No current focus.</div>}
            </Section>
            <Section testid="gtm-rail-next-gate" label="Next Gate" Icon={ShieldCheck}>
                {nextGate ? <MilestoneLine m={nextGate} /> : <div className="text-[12px] text-qoryn-textDim">No pending gate.</div>}
            </Section>
            <Section testid="gtm-rail-next-decision" label="Next Founder Decision" Icon={Sparkles}>
                {nextDecision ? <MilestoneLine m={nextDecision} /> : <div className="text-[12px] text-qoryn-textDim">No decision queued.</div>}
            </Section>
            <Section testid="gtm-rail-blockers" label="Current Blockers" Icon={OctagonX}>
                {blockers.length === 0 ? (
                    <div data-testid="gtm-no-blockers" className="text-[12px] font-mono-ui tracking-widest uppercase text-emerald-300/90">
                        No launch-critical blockers
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {blockers.slice(0, 4).map((m) => (
                            <li key={m.code}><MilestoneLine m={m} /></li>
                        ))}
                    </ul>
                )}
            </Section>
            <Section testid="gtm-rail-launch-critical" label="Launch-Critical Remaining" Icon={ArrowRight}>
                <div className="flex items-baseline gap-2">
                    <span data-testid="gtm-rail-launch-critical-count" className="font-display text-[32px] leading-none text-qoryn-text tracking-tight">
                        {launchCriticalRemaining.length}
                    </span>
                    <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                        milestones
                    </span>
                </div>
            </Section>
            <Section testid="gtm-rail-recently-completed" label="Recently Completed" Icon={CheckCircle2}>
                {recentCompleted.length === 0 ? (
                    <div className="text-[12px] text-qoryn-textDim">Nothing completed yet.</div>
                ) : (
                    <ul className="space-y-2">
                        {recentCompleted.map((m) => (
                            <li key={m.code} className="text-[12px] text-qoryn-text truncate">
                                {m.title}
                            </li>
                        ))}
                    </ul>
                )}
            </Section>
        </div>
    );
}
